import { Defs, Document, Image, LinearGradient, Link, Page, Rect, Stop, StyleSheet, Svg, Text, View } from '@react-pdf/renderer'
import { iCartaConvite } from '../../../../CartaConvite'

interface iProps {
  cartas: iCartaConvite[]
}

const estilos = StyleSheet.create({
  pagina: { padding: 32, fontSize: 10, color: '#2B2B2B', lineHeight: 1.6 },
  linha: { flexDirection: 'row', gap: 20 },
  colunaMidia: { width: '25%', gap: 12 },
  colunaTexto: { width: '75%', gap: 10 },
  imagem: { width: '100%', height: 90, objectFit: 'contain' },
  qrCode: { width: '100%', height: 120, objectFit: 'contain' },
  titulo: { fontSize: 16, fontWeight: 700, textAlign: 'center' },
  paragrafo: { marginBottom: 8 },
  link: { color: '#1961E0', textDecoration: 'underline' }
})

const TAGS_BLOCO = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'UL', 'OL']

const estilosPorClasse: Record<string, object> = {
  link_pec: { color: '#1961E0', textDecoration: 'underline' },
  senha_pec: { color: '#1961E0', fontSize: 13, fontWeight: 700 },
  titulo_senha_pec: { fontWeight: 700 },
  'text-info': { color: '#1961E0' },
  'text-center': { textAlign: 'center' }
}

function EstiloDaTag(elemento: Element): NonNullable<React.ComponentProps<typeof Link>['style']> {
  const porClasse = Array.from(elemento.classList)
    .map(classe => estilosPorClasse[classe])
    .filter(e => e !== undefined)

  const porTag = ['B', 'STRONG'].includes(elemento.tagName)
    ? { fontWeight: 700 }
    : ['I', 'EM'].includes(elemento.tagName)
        ? { fontStyle: 'italic' }
        : elemento.tagName === 'U'
          ? { textDecoration: 'underline' }
          : {}

  return Object.assign({}, porTag, ...porClasse)
}

function RenderizarInline(no: ChildNode, chave: string): React.ReactNode {
  if (no.nodeType === 3) {
    return no.textContent
  }

  if (no.nodeType !== 1) {
    return null
  }

  const elemento = no as Element
  if (elemento.tagName === 'BR') {
    return '\n'
  }

  const filhos = Array.from(elemento.childNodes).map((filho, i) => RenderizarInline(filho, `${chave}-${i}`))

  if (elemento.tagName === 'A') {
    return <Link key={chave} src={elemento.getAttribute('href') ?? ''} style={estilos.link}>{filhos}</Link>
  }

  return <Text key={chave} style={EstiloDaTag(elemento)}>{filhos}</Text>
}

function RenderizarBloco(no: ChildNode, chave: string): React.ReactNode {
  if (no.nodeType === 3) {
    const texto = no.textContent?.trim()
    return texto ? <Text key={chave} style={estilos.paragrafo}>{texto}</Text> : null
  }

  if (no.nodeType !== 1) {
    return null
  }

  const elemento = no as Element
  if (elemento.tagName === 'BR') {
    return null
  }

  const temFilhoBloco = Array.from(elemento.children).some(filho => TAGS_BLOCO.includes(filho.tagName))
  if (temFilhoBloco) {
    return <View key={chave}>{Array.from(elemento.childNodes).map((filho, i) => RenderizarBloco(filho, `${chave}-${i}`))}</View>
  }

  const filhos = Array.from(elemento.childNodes).map((filho, i) => RenderizarInline(filho, `${chave}-${i}`))
  return <Text key={chave} style={Object.assign({}, estilos.paragrafo, EstiloDaTag(elemento))}>{filhos}</Text>
}

function Mensagem({ html }: { html: string }): JSX.Element {
  const documento = new DOMParser().parseFromString(html, 'text/html')
  return <View>{Array.from(documento.body.childNodes).map((no, i) => RenderizarBloco(no, `m${i}`))}</View>
}

export const RelatorioPDF: React.FC<iProps> = ({ cartas }) => {
  return (
    <Document>
      {cartas.map((carta, index) => (
        <Page key={index} size={'A4'} style={estilos.pagina}>
          <View style={estilos.linha}>
            <View style={estilos.colunaMidia}>
              {carta.logoEmpresa && <Image src={carta.logoEmpresa} style={estilos.imagem} />}
              {carta.urlImagem && <Image src={carta.urlImagem} style={estilos.imagem} />}
              {carta.qrCode && <Image src={carta.qrCode} style={estilos.qrCode} />}
            </View>
            <View style={estilos.colunaTexto}>
              <Text style={estilos.titulo}>PESQUISA DE ENGAJAMENTO</Text>
              <Svg viewBox={'0 0 100 6'} preserveAspectRatio={'none'} style={{ width: '100%', height: 6 }}>
                <Defs>
                  <LinearGradient id={'degradeCartaConvite'} x1={'0'} y1={'0'} x2={'1'} y2={'0'}>
                    <Stop offset={'0'} stopColor={'#1961E0'} />
                    <Stop offset={'1'} stopColor={'#66046B'} />
                  </LinearGradient>
                </Defs>
                <Rect x={'0'} y={'0'} width={'100'} height={'6'} fill={'url(#degradeCartaConvite)'} />
              </Svg>
              <Mensagem html={carta.msgConvite ?? ''} />
            </View>
          </View>
        </Page>
      ))}
    </Document>
  )
}
